import { AccessLevel, Resource, User } from "@/types/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ReactNode,
    useLocation,
    useMatch,
    useNavigate,
    useSearch,
} from "@tanstack/react-router";
import useRequest from "../request/useRequest";
import AuthContext from "./AuthContext";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { datadogRum } from "@datadog/browser-rum";
import useIsMobile from "../utils/useIsMobile";
import { useTranslation } from "react-i18next";
import { Organization } from "@/types/organization";

interface LoginResponse {
    access: string;
    refresh: string;
    user: LoginUser;
}

interface LoginUser {
    pk: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
}

const DEFAULT_ACCESS_CONTROL: Record<Resource, AccessLevel> = {
    si_trendline: AccessLevel.SUMMARY,
    si_bar: AccessLevel.SUMMARY,
    virtual_si: AccessLevel.SUMMARY,
    generate_rx: AccessLevel.SUMMARY,
    growth_model: AccessLevel.SUMMARY,
    insights_configuration: AccessLevel.SUMMARY,
    manually_add_plots: AccessLevel.SUMMARY,
    simulate_rx: AccessLevel.SUMMARY,
    soil_properties: AccessLevel.SUMMARY,
    tissue_properties: AccessLevel.SUMMARY,
    management_zones: AccessLevel.SUMMARY,
    treatment_sectors: AccessLevel.SUMMARY,
    imagery_timeline: AccessLevel.SUMMARY,
    tour: AccessLevel.SUMMARY,
    irrigation: AccessLevel.SUMMARY,
};

function AuthProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient();
    const isMobile = useIsMobile();
    const { axiosRequest, useGetRequest } = useRequest();
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [userState, setUserState] = useState<
        (LoginUser & { id?: number }) | undefined
    >(undefined);
    const refreshAttemptedRef = useRef(false);

    const { redirect } = useSearch({
        strict: false,
    }) as { redirect?: string };

    const match = useMatch({
        from: "/(auth)/auth",
        shouldThrow: false,
    });

    const isUnauthenticatedRoute = useMemo(() => {
        return (
            location.pathname.includes("/auth") ||
            location.pathname.includes("/register") ||
            location.pathname.includes("/password/reset")
        );
    }, [location.pathname]);

    const {
        data: user,
        isSuccess,
        isError,
        isPending,
        isFetched,
        refetch,
    } = useGetRequest<User>({
        url: `users/${userState?.pk ?? userState?.id}/`,
        enabled: !!(userState?.pk ?? userState?.id),
    });

    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: user.id,
                username: user.username,
                email: user.email,
            });
            datadogRum.setUser({
                id: user.id.toString(),
                email: user.email,
                name: user.full_name,
                oldNtime: localStorage.getItem("oldNTime"),
                organization_name: (user.organization as Organization).name,
                organization_type: (user.organization as Organization)
                    .group_type,
            });
        } else {
            Sentry.setUser(null);
            datadogRum.clearUser();
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            i18n.changeLanguage(user.language_preference ?? "en");
            localStorage.setItem(
                "unit_preference",
                user.unit_preference ?? "imperial",
            );
        }
    }, [user, i18n]);

    const signInMutate = useMutation({
        mutationFn: (data: { username: string; password: string }) =>
            axiosRequest<LoginResponse>({
                url: "api-auth/login/",
                method: "post",
                data,
            }),
    });

    useEffect(() => {
        if (user) {
            Cookies.set("user-id", user.id.toString(), {
                path: "/",
            });
            Cookies.set("user-type", user.account_type, {
                path: "/",
            });
        } else if (isFetched && !user) {
            Cookies.remove("user-id");
            Cookies.remove("user-type");
        }
    }, [isFetched, user, userState]);

    const runStartup = useCallback(async () => {
        const handleRedirect = () => {
            if (isUnauthenticatedRoute) {
                return;
            }
            if (match) {
                return;
            }
            if (
                window.localStorage.getItem("oldNTime") === "false" ||
                isMobile
            ) {
                navigate({
                    to: "/auth",
                    search: {
                        redirect: location.pathname.replace("/ntimev1", ""),
                    },
                });
            } else {
                navigate({
                    to: "/ntimev1/$",
                    params: { _splat: "auth" },
                    search: location.search,
                });
            }
        };
        try {
            refreshAttemptedRef.current = true;

            const refresh_token = window.localStorage.getItem("refresh_token");
            if (!refresh_token) {
                handleRedirect();
                return;
            }
            setUserState(JSON.parse(window.localStorage.getItem("user") ?? ""));

            const response = await axiosRequest<{
                refresh: string;
                access: string;
            }>(
                {
                    url: `api-auth/token/refresh/`,
                    method: "post",
                    data: { refresh_token },
                },
                {
                    ignoreRedirect: true,
                },
            );

            if (response.refresh && response.access) {
                window.localStorage.setItem("auth_token", response.access);
                window.localStorage.setItem("refresh_token", response.refresh);
            } else {
                handleRedirect();
                return;
            }
        } catch {
            window.localStorage.removeItem("auth_token");
            window.localStorage.removeItem("refresh_token");
            window.localStorage.removeItem("user");
            handleRedirect();
        }
    }, [
        match,
        isMobile,
        navigate,
        axiosRequest,
        isUnauthenticatedRoute,
        location.pathname,
        location.search,
    ]);

    useEffect(() => {
        if (refreshAttemptedRef.current) return;

        runStartup();
    }, [axiosRequest, navigate, runStartup]);

    const signIn = useCallback(
        (data: { username: string; password: string }) => {
            signInMutate.mutate(
                {
                    username: data.username.toLowerCase(),
                    password: data.password,
                },
                {
                    onSuccess: (response) => {
                        window.localStorage.setItem(
                            "auth_token",
                            response.access,
                        );
                        window.localStorage.setItem(
                            "refresh_token",
                            response.refresh,
                        );
                        window.localStorage.setItem(
                            "user",
                            JSON.stringify(response.user),
                        );
                        Cookies.set("ntime-auth", response.access, {
                            path: "/",
                        });
                        Cookies.set("ntime-refresh", response.refresh, {
                            path: "/",
                        });
                        Cookies.set("user-id", response.user.pk.toString(), {
                            path: "/",
                        });

                        setUserState(response.user);
                        refetch();

                        navigate({
                            to: redirect ?? "/dashboard/home",
                        });
                    },
                },
            );
        },
        [navigate, refetch, redirect, signInMutate],
    );

    const logout = useCallback(() => {
        axiosRequest(
            {
                url: `api/token/blacklist/`,
                method: "post",
                data: {
                    refresh: window.localStorage.getItem("refresh_token"),
                },
            },
            {
                ignoreRedirect: true,
            },
        );
        window.localStorage.removeItem("auth_token");
        window.localStorage.removeItem("refresh_token");
        window.localStorage.removeItem("user");
        setUserState(undefined);
        Sentry.setUser(null);
        datadogRum.clearUser();
        queryClient.invalidateQueries();
        navigate({ to: "/auth" });
    }, [axiosRequest, navigate, queryClient]);

    const userIsSentinel = useMemo(
        () => user?.account_type === "sentinel",
        [user?.account_type],
    );

    const isConsultantOrSentinel = useMemo(
        () =>
            user?.account_type === "sentinel" ||
            user?.account_type === "consultant",
        [user?.account_type],
    );
    const isConsultant = useMemo(
        () => user?.account_type === "consultant",
        [user?.account_type],
    );

    const isGrower = useMemo(
        () => user?.account_type === "grower",
        [user?.account_type],
    );
    const [forcedLevel, setForcedLevel] = useState<AccessLevel | null>(null);

    const accessControl = useMemo(() => {
        if (forcedLevel) {
            return Object.entries(DEFAULT_ACCESS_CONTROL).reduce(
                (acc, [key]) => {
                    acc[key as Resource] = forcedLevel;
                    return acc;
                },
                {} as Record<Resource, AccessLevel>,
            );
        }
        return user?.access_control ?? DEFAULT_ACCESS_CONTROL;
    }, [user?.access_control, forcedLevel]);

    const isAtLeastSummary = useCallback(
        (resource: Resource) => {
            return [
                AccessLevel.SUMMARY,
                AccessLevel.DETAIL,
                AccessLevel.SENTINEL,
            ].includes(accessControl[resource]);
        },
        [accessControl],
    );

    const isAtLeastDetail = useCallback(
        (resource: Resource) => {
            return [AccessLevel.DETAIL, AccessLevel.SENTINEL].includes(
                accessControl[resource],
            );
        },
        [accessControl],
    );

    const isAtLeastSentinel = useCallback(
        (resource: Resource) => {
            return [AccessLevel.SENTINEL].includes(accessControl[resource]);
        },
        [accessControl],
    );

    return (
        <AuthContext.Provider
            value={{
                user,
                isError,
                isPending,
                isSuccess,
                isFetched,
                signIn,
                logout,
                refreshUser: refetch,
                signInMutate,
                isConsultantOrSentinel,
                isConsultant,
                isGrower,
                userIsSentinel,
                runStartup,
                accessControl,
                isAtLeastSummary,
                isAtLeastDetail,
                isAtLeastSentinel,
                forcedLevel,
                setForcedLevel,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export default AuthProvider;
